import React, { useState, useRef } from "react";
import { Input } from "antd";
import type { InputProps, InputRef } from "antd";

interface AmountInputProps extends Omit<InputProps, "value" | "onChange"> {
  value?: number;
  onChange?: (value: number | undefined) => void;
}

/**
 * 金额输入框组件
 * - 聚焦时：显示纯数字，弹出数字键盘（iOS 优化）
 * - 失焦时：显示千分位格式
 * - iPad 优化：使用 type="tel" 确保在 iPad 上也能弹出数字键盘
 */
export const AmountInput: React.FC<AmountInputProps> = ({ value, onChange, suffix, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<InputRef>(null);

  const formatNumber = (num: number): string => {
    return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // 聚焦时显示纯数字，失焦时显示千分位
  const displayValue = isFocused
    ? (value !== undefined && value !== null ? String(value) : "")
    : (value !== undefined && value !== null ? formatNumber(value) : "");

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    props.onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // 只允许数字、小数点和负号
    const sanitized = inputValue.replace(/[^\d.-]/g, '');

    // 解析为数字
    if (sanitized === "" || sanitized === "-") {
      onChange?.(undefined);
    } else {
      const parsed = Number(sanitized);
      onChange?.(isNaN(parsed) ? undefined : parsed);
    }
  };

  // 如果有 suffix，使用自定义布局
  if (suffix) {
    return (
      <div
        style={{
          position: "relative",
          display: "inline-block",
          width: props.style?.width || "100%",
        }}
      >
        <Input
          ref={inputRef}
          {...props}
          // 关键：使用 type="tel" 在 iPad 上更可靠地触发数字键盘
          // tel 类型在所有 iOS 设备上都会显示数字键盘（包括 iPad）
          type={isFocused ? "tel" : "text"}
          inputMode="decimal"
          enterKeyHint="done"
          autoComplete="off"
          // 强制使用英文键盘布局
          lang="en"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            ...props.style,
            paddingRight: "50px",
          }}
        />
        {suffix && (
          <span
            style={{
              position: "absolute",
              right: "11px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "rgba(0, 0, 0, 0.45)",
              pointerEvents: "none",
            }}
          >
            {suffix}
          </span>
        )}
      </div>
    );
  }

  // 没有 suffix 时使用标准 Input
  return (
    <Input
      ref={inputRef}
      {...props}
      type={isFocused ? "tel" : "text"}
      inputMode="decimal"
      enterKeyHint="done"
      autoComplete="off"
      // 强制使用英文键盘布局
      lang="en"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};
